import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import {
  trigger,
  state,
  style,
  transition,
  animate,
} from '@angular/animations';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AppIconComponent } from '../../../lib/ui';

export interface MenuItem {
  title: string;
  icon: string;
  route: string;
  roles: string[];
  category: string;
  isNew?: boolean;
  newBadge?: string;
  isUpdated?: boolean;
  description?: string;
}

export interface MenuCategory {
  id: string;
  name: string;
  icon: string;
}

@Component({
  selector: 'app-navigation-menu-modern',
  standalone: true,
  imports: [CommonModule, RouterModule, AppIconComponent],
  templateUrl: './navigation-menu-modern.component.html',
  styleUrls: ['./navigation-menu-modern.component.scss'],
  animations: [
    trigger('slideInOut', [
      state('in', style({ height: '*', opacity: 1 })),
      state('out', style({ height: '0px', opacity: 0 })),
      transition('in => out', animate('300ms ease-in-out')),
      transition('out => in', animate('300ms ease-in-out')),
    ]),
  ],
})
export class NavigationMenuModernComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() menuItems: MenuItem[] = [];
  @Input() categories: MenuCategory[] = [];
  @Input() userRoles: string[] = [];
  @Input() isMenuExpanded = true;

  @Output() itemClick = new EventEmitter<MenuItem>();

  // Estado de expansão das categorias
  expandedCategories = new Set<string>();
  private destroy$ = new Subject<void>();
  currentRoute = '';

  constructor(private router: Router) {}

  ngOnInit(): void {
    console.log('Navigation Menu Initialized with:', {
      menuItems: this.menuItems.length,
      categories: this.categories.length,
      userRoles: this.userRoles,
    });

    console.log('User Roles:', JSON.stringify(this.userRoles));
    console.log('Visible Categories:', this.getVisibleCategories().length);
    console.log(
      'Menu Items por Categoria:',
      this.categories.map((cat) => ({
        categoria: cat.name,
        itens: this.getMenuItemsByCategory(cat.id).length,
      }))
    );

    // Monitorar mudanças de rota para expandir categoria automaticamente
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.urlAfterRedirects;
        this.updateExpandedCategoriesBasedOnRoute();
        this.scrollToTop();
      });

    // Inicializar com a rota atual
    this.currentRoute = this.router.url;
    this.updateExpandedCategoriesBasedOnRoute();

    // Inicializar as principais categorias como expandidas por padrão
    const defaultExpandedCategories = ['inventory', 'sales', 'settings'];
    defaultExpandedCategories.forEach((catId) => {
      if (this.categories.find((cat) => cat.id === catId)) {
        this.expandedCategories.add(catId);
      }
    });

    // For debugging - if roles array is empty, show a warning
    if (!this.userRoles || this.userRoles.length === 0) {
      console.warn(
        '⚠️ Navigation Menu received empty userRoles array! Menu items may not display correctly.'
      );
    }

    // Executar diagnóstico do menu
    setTimeout(() => {
      this.debugMenu();
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar mudanças nos roles e atualizar o componente
    if (changes['userRoles']) {
      console.log(
        'userRoles changed:',
        JSON.stringify(changes['userRoles'].previousValue),
        '→',
        JSON.stringify(changes['userRoles'].currentValue)
      );
    }

    // Detectar mudanças nos itens de menu
    if (changes['menuItems']) {
      console.log(
        'menuItems changed: previous length =',
        changes['menuItems'].previousValue?.length,
        ', current length =',
        changes['menuItems'].currentValue?.length
      );
    }
  }

  hasAccess(roles: string[]): boolean {
    /*
    // Se não houver roles de usuário definidos, considerar acesso liberado para desenvolvimento
    if (!this.userRoles || this.userRoles.length === 0) {
      console.warn('No userRoles defined, allowing access to all menu items');
      return true;
    }*/

    // ESTOKAI_OWNER tem acesso a tudo, independentemente dos roles do item
    if (this.userRoles.includes('ESTOKAI_OWNER')) {
      return true;
    }

    // Owner (role moderna) tem acesso a tudo igual ao dono
    if (
      this.userRoles.includes('owner') &&
      roles.some((r) => ['owner', 'dono', 'gerente', 'vendedor'].includes(r))
    ) {
      return true;
    }

    // Dono tem acesso a quase tudo
    if (
      this.userRoles.includes('dono') &&
      roles.some((r) => ['dono', 'gerente', 'vendedor'].includes(r))
    ) {
      return true;
    }

    // Gerente tem acesso a itens de gerente e vendedor
    if (
      this.userRoles.includes('gerente') &&
      roles.some((r) => ['gerente', 'vendedor'].includes(r))
    ) {
      return true;
    }

    // Para outros perfis, verificar se há interseção entre os roles do menu e do usuário
    return roles.some((role) => this.userRoles.includes(role));
  }

  getMenuItemsByCategory(categoryId: string): MenuItem[] {
    const items = this.menuItems.filter(
      (item) => item.category === categoryId && this.hasAccess(item.roles)
    );

    // Debug específico para category settings
    if (categoryId === 'settings') {
      console.log(
        `🔧 Settings category items:`,
        items.map((i) => ({ title: i.title, route: i.route, roles: i.roles }))
      );
      console.log(`👤 User roles:`, this.userRoles);
    }

    return items;
  }

  // Método para obter itens sem categoria (Dashboard)
  getMenuItemsWithoutCategory(): MenuItem[] {
    return this.menuItems.filter(
      (item) =>
        (!item.category || item.category === '') && this.hasAccess(item.roles)
    );
  }

  getVisibleCategories(): MenuCategory[] {
    return this.categories.filter((category) => {
      const hasVisibleItems =
        this.getMenuItemsByCategory(category.id).length > 0;
      return hasVisibleItems;
    });
  }

  isLastCategory(category: MenuCategory): boolean {
    const visibleCategories = this.getVisibleCategories();
    return visibleCategories[visibleCategories.length - 1] === category;
  }

  onItemClick(item: MenuItem): void {
    console.log(`🖱️ Menu item clicked: ${item.title} (${item.route})`);

    // Verifica se é uma funcionalidade em breve
    if (this.isComingSoonFeature(item.route)) {
      console.log(`Funcionalidade "${item.title}" em desenvolvimento`);
      // Aqui você poderia adicionar um alerta ou toast notification
      // por exemplo: this.toastService.info(`A funcionalidade "${item.title}" estará disponível em breve!`);

      // Emite o evento apenas para logging, mas não navega - usamos string vazia em vez de null
      this.itemClick.emit({ ...item, route: '' });

      // Previne navegação (já tratado no template também)
      return;
    }

    console.log(`✅ Navigating to: ${item.route}`);
    this.itemClick.emit(item);
  }

  // Métodos para controle de toggle das categorias
  toggleCategory(categoryId: string): void {
    if (this.expandedCategories.has(categoryId)) {
      this.expandedCategories.delete(categoryId);
    } else {
      this.expandedCategories.add(categoryId);
    }
  }

  isCategoryExpanded(categoryId: string): boolean {
    return this.expandedCategories.has(categoryId);
  }

  expandAllCategories(): void {
    this.categories.forEach((cat) => this.expandedCategories.add(cat.id));
  }

  collapseAllCategories(): void {
    this.expandedCategories.clear();
  }

  // Atualizar categorias expandidas baseado na rota ativa
  private updateExpandedCategoriesBasedOnRoute(): void {
    // Primeiro, encontrar o item de menu que melhor corresponde à rota atual
    let activeMenuItem = this.menuItems.find(
      (item) => item.route !== '/' && this.currentRoute === item.route
    );

    // Se não encontrou correspondência exata, procurar por início de rota
    if (!activeMenuItem) {
      // Ordenar por tamanho da rota para pegar a mais específica primeiro
      const sortedItems = this.menuItems
        .filter((item) => item.route !== '/' && item.route !== '')
        .sort((a, b) => b.route.length - a.route.length);

      activeMenuItem = sortedItems.find((item) =>
        this.currentRoute.startsWith(item.route)
      );
    }

    if (activeMenuItem && activeMenuItem.category) {
      // Expandir a categoria do item ativo
      this.expandedCategories.add(activeMenuItem.category);
      console.log(
        `✅ Auto-expanded category: ${activeMenuItem.category} for route: ${this.currentRoute}`
      );
    } else {
      console.log(`ℹ️ No category to expand for route: ${this.currentRoute}`);
    }
  }

  // Scroll to top da página (Mobile First)
  private scrollToTop(): void {
    // Aguardar um frame para garantir que o componente foi renderizado
    requestAnimationFrame(() => {
      // Múltiplas estratégias para garantir scroll to top em diferentes browsers/dispositivos
      try {
        // Estratégia principal para navegadores modernos
        window.scrollTo({ top: 0, behavior: 'smooth' });

        // Fallback para navegadores antigos e mobile
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;

        // Para dispositivos móveis, também resetar scroll de containers principais
        const mainContent = document.querySelector('main');
        const routerOutlet = document.querySelector('router-outlet');
        const appRoot = document.querySelector('router-outlet')?.parentElement;

        [mainContent, routerOutlet, appRoot].forEach((element) => {
          if (element && 'scrollTop' in element) {
            element.scrollTop = 0;
          }
        });

        console.log('📱 Scrolled to top (Mobile First)');
      } catch (error) {
        console.warn('⚠️ Error scrolling to top:', error);
      }
    });
  }

  // Verificar se um item está ativo baseado na rota atual
  isMenuItemActive(item: MenuItem): boolean {
    if (!this.currentRoute || !item.route) return false;

    // Para rotas exatas como dashboard
    if (item.route === '/dashboard' && this.currentRoute === '/dashboard') {
      return true;
    }

    // Para a rota raiz, só ativar se for exatamente a rota raiz
    if (item.route === '/' && this.currentRoute === '/') {
      return true;
    }

    // Para outras rotas, verificar se a rota atual começa com a rota do item
    // mas não ativar para a rota raiz
    if (item.route !== '/' && item.route !== '') {
      return this.currentRoute.startsWith(item.route);
    }

    return false;
  }

  // Método para debug - verificar se há itens no menu para o perfil atual
  debugMenu(): void {
    console.log('=== DIAGNÓSTICO DE MENU ===');
    console.log('User Roles:', this.userRoles);

    // Verificar cada categoria
    this.categories.forEach((category) => {
      const items = this.menuItems.filter(
        (item) => item.category === category.id
      );
      const accessibleItems = items.filter((item) =>
        this.hasAccess(item.roles)
      );

      console.log(`Categoria ${category.name} (${category.id}):`, {
        totalItems: items.length,
        accessibleItems: accessibleItems.length,
        itemsWithAccess: accessibleItems.map((i) => i.title),
      });
    });

    // Verificar os roles em cada item do menu
    const rolesMappings = this.menuItems.reduce((acc, item) => {
      item.roles.forEach((role) => {
        if (!acc[role]) acc[role] = [];
        acc[role].push(item.title);
      });
      return acc;
    }, {} as Record<string, string[]>);

    console.log('Items por role:', rolesMappings);

    // Verificar se ESTOKAI_OWNER tem acesso
    const isEstokaiOwner = this.userRoles.includes('ESTOKAI_OWNER');
    console.log('É ESTOKAI_OWNER:', isEstokaiOwner);

    // TESTE ESPECIAL: Verificar acesso ao primeiro item de cada categoria como ESTOKAI_OWNER
    if (isEstokaiOwner) {
      console.log('=== TESTE DE ACESSO ESTOKAI_OWNER ===');
      this.categories.forEach((category) => {
        const firstItem = this.menuItems.find(
          (item) => item.category === category.id
        );
        if (firstItem) {
          console.log(`Teste categoria ${category.name}:`, {
            item: firstItem.title,
            roles: firstItem.roles,
            hasAccess: this.hasAccess(firstItem.roles),
          });
        }
      });
    }

    console.log('=== FIM DO DIAGNÓSTICO ===');
  }

  /**
   * Verifica se uma funcionalidade está marcada como "Em breve"
   * @param route Rota do item de menu
   */
  isComingSoonFeature(route: string): boolean {
    // Lista de rotas que têm componentes implementados
    const implementedRoutes = [
      '/dashboard',
      '/products',
      '/categories',
      '/sales',
      '/suppliers',
      '/purchase-orders',
      '/accounts-receivable',
      '/accounts-payable',
      '/accounts-payable-reports',
      '/cash-flow',
      '/scanner',
      '/brands',
      '/inventory-locations',
      '/pdv',
      '/reports',
      '/reports/dashboard',
      '/reports/sales',
      '/reports/stock',
      '/reports/top-products',
      '/reports/financial',
      '/onboarding',
      '/users',
      '/customers',
      '/integrations',
      '/settings',
    ];

    // Debug: log das rotas being checked
    const isComingSoon = !implementedRoutes.includes(route);
    if (route.includes('/reports') || route.includes('/integrations')) {
      console.log(
        `🔍 Route check: ${route} | Implemented: ${implementedRoutes.includes(
          route
        )} | Coming Soon: ${isComingSoon}`
      );
    }

    // Se a rota não estiver na lista de implementadas, é "Em breve"
    return isComingSoon;
  }

  isNewFeature(route: string): boolean {
    return this.menuItems.some((item) => item.route === route && item.isNew);
  }

  isUpdatedFeature(route: string): boolean {
    return this.menuItems.some(
      (item) => item.route === route && item.isUpdated
    );
  }

  getFeatureDescription(route: string): string {
    const item = this.menuItems.find((item) => item.route === route);
    return item?.description || '';
  }

  getNewBadgeText(item: any): string {
    return item.newBadge || 'NOVO';
  }
}
